import React, { useEffect, useState, useRef } from 'react';
import { Popover } from 'react-tiny-popover';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import SmoothCollapse from 'react-smooth-collapse';
import ChevronDownIcon from 'calcite-ui-icons-react/ChevronDownIcon';
import Select from '../select';

import MapController from '../../../../controllers/MapController';

import { RootState } from '../../../../reducers/app';
import config from '../../../../config/config';

import './my-layer.scss';
import './satellite-layer.scss'
import LayerTooltip from './layer-tooltip';
import InfoSvg from '../../../../assets/info-icon';
import { IOptionsType } from '../../../../types/react-select-options';
import { IRasterFunctionInfo } from '../../../../types/layers';

const SatelliteLayer = ({ layer, setSelectedPopover, selectedPopover }: { layer: any, setSelectedPopover: Function, selectedPopover: string }) => {
  // LOCAL STATE
  const [sectionOpen, setSectionOpen] = useState<boolean>(false);
  const [selectedRenderingOption, setSelectedRenderingOption] = useState<IOptionsType>({
    value: 'Natural Color with DRA',
    label: 'Natural Color with DRA',
  });
  const [renderingOptions, setRenderingOptions] = useState<Array<IOptionsType>>([]);
  const timesliderRef = useRef(null);

  // REDUX
  const visibleLayers = useSelector((state: RootState) => state.mapview.visibleLayers);
  const timeSlidersEnabled = useSelector((state: RootState) => state.mapview.timeSlidersEnabled);
  const swipeToolActive = useSelector((state: RootState) => state.mapview.swipeToolActive);
  const timelineOpen = useSelector((state: RootState) => state.auditdata.timelineOpen);
  
  // REFS
  const legendContainerRef = React.createRef<any>();

  const handleOutsideClick = (e: any, layerId: string) => {
    const esriElement = [...e.target.classList].some(e => e.includes('esri-'))
    if (!esriElement && (layerId !== 'sentinel' && layerId !== 'landsat')) setSelectedPopover('')  
  }

  useEffect(() => {
    if (layer.id === 'sentinel' || layer.id === 'landsat') {
      MapController.updateRenderingOption(layer.id, selectedRenderingOption.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRenderingOption]);

  useEffect(() => {
    MapController.fetchLegendInfo(legendContainerRef.current, layer.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (layer.id === 'landsat' || layer.id === 'sentinel') {
      const fetchLayerJson = async () => {
        const layerConfig = config.layers.find((configLayer) => configLayer.options.id === layer.id);
        if (layerConfig) {
          const { options, renderingRules } = layerConfig;
          const res = await fetch(`${options.url}/?f=pjson`);
          const body = await res.json();
          if(body.hasOwnProperty('rasterFunctionInfos')){
            const rasterFunctionInfos = body.rasterFunctionInfos?.map((rf: IRasterFunctionInfo) => rf.name);
            if (renderingRules && rasterFunctionInfos.length > 0) {
              const availableRenderingRules = renderingRules?.filter((rule) => rasterFunctionInfos.includes(rule));
              setRenderingOptions(
                availableRenderingRules?.map((option: string) => {
                  return { value: option, label: option };
                })
              );
            }
          }
        }
      };
      fetchLayerJson();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timesliderRef === null) return
    // MapController.setupTimeSliders(layer.id)

  }, [timesliderRef])

  return (
    <div className='layer-container' key={layer.id}>
      <div className='layer'>
        <div className='layer-title__wrapper'>
          <input
            type='checkbox'
            // id={`{layer.id}`}
            name={layer.id}
            checked={visibleLayers.includes(layer.id)}
            onChange={() => MapController.updateLayerVisibility(layer.id, !visibleLayers.includes(layer.id))}
          />
          <div className='layer-title'>{layer.title}</div>
        </div>
        <div className='tools'>
          {layer.description && layer.description.length > 0 && (
            <LayerTooltip tip={layer.description}>
              <InfoSvg />
            </LayerTooltip>
          )}
          <Popover
            isOpen={true}
            positions={['right']}
            onClickOutside={(e) => handleOutsideClick(e, layer.id)}
            content={
              <div className={`ts ${selectedPopover === layer.id ? '' : 'closed'}`}><div id={`${layer.id}`} /></div>}
          >
            <div data-tip data-for='sadFace'>
              <button disabled={!timeSlidersEnabled} onClick={() => setSelectedPopover(selectedPopover === layer.id ? '' : layer.id)} className='esri-icon-time-clock timeslider'></button>
            </div>
          </Popover>
          {!timeSlidersEnabled && !timelineOpen && !swipeToolActive &&
            <ReactTooltip className='tooltip' place='right' type='success' id='sadFace' effect='solid'>
              <span>Zoom in to enable timeslider</span>
            </ReactTooltip>
          }
          {timelineOpen && timeSlidersEnabled &&
            <ReactTooltip className='tooltip' place='right' type='success' id='sadFace' effect='solid'>
              <span>Timeslider unavailable while analyzing imagery</span>
            </ReactTooltip>
          }
          {swipeToolActive && timeSlidersEnabled &&
            <ReactTooltip className='tooltip' place='right' type='success' id='sadFace' effect='solid'>
              <span>Timeslider unavailable while swipe tool is active</span>
            </ReactTooltip>
          }
          <ChevronDownIcon
            className={sectionOpen ? 'open' : ''}
            size={24}
            onClick={() => setSectionOpen(!sectionOpen)}
          />
        </div>
      </div>
      <SmoothCollapse className='hansen-container' expanded={sectionOpen} allowOverflowWhenOpen eagerRender={true}>
        {layer.id !== 'sentinel' && layer.id !== 'landsat' ? (
          <div ref={legendContainerRef} />
        ) : (
          renderingOptions.length > 0 && (
            <>
              <div className='rendering-rule-container'>

                <Select
                  isMulti={false}
                  options={renderingOptions}
                  value={selectedRenderingOption}
                  onChange={setSelectedRenderingOption}
                  isDisabled={false}
                  isClearable={false}
                  placeholder='Select Rendering Option'
                />
                <LayerTooltip
                  id="rendering-tip"
                  tip={layer.id === 'landsat' ? config.helpertext.landsatRenderingRuleInfo : config.helpertext.sentinelRenderingRuleInfo}>
                  <InfoSvg />
                </LayerTooltip>
                <ReactTooltip
                  className='tooltip'
                  id='rendering-tip'
                  place='right'
                  type='success'
                  effect='solid'
                  clickable={true} />
              </div>
            </>
          )
        )}
      </SmoothCollapse>
    </div>
  );
};

export default SatelliteLayer;
